import { type DetailsReturn } from '@shared/all/domains/user';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';

import { type UserAction, userActions } from '../context/context';
import { useUserDispatch, useUserState } from '../context/UserContext';
import { NOTIFICATIONS_UNREAD_KEY } from '../queryKeys';

// Slices of the user, for components that need less than all of it (see
// context/UserContext.tsx): they rerender only when their slice changes.
export {
  useCurrentUserId,
  useIsAuthenticated,
  useUserIdentity
} from '../context/UserContext';

const createUserActions = (
  dispatch: (action: UserAction) => void,
  queryClient: QueryClient
) => ({
  setIsAuthenticated: (isAuthenticated: boolean) => {
    dispatch({
      type: userActions.SET_IS_AUTHENTICATED,
      payload: isAuthenticated
    });
  },

  setUser: (user: DetailsReturn) => {
    dispatch({ type: userActions.SET_USER, payload: user });
  },

  addTrons: (trons: number) => {
    dispatch({ type: userActions.ADD_TRONS, payload: trons });
  },

  setPendingNotificationsCount: (count: number) => {
    queryClient.setQueryData(NOTIFICATIONS_UNREAD_KEY, count);
  }
});

/** What changes the user. Subscribes to no user state. */
export const useUserActions = () =>
  createUserActions(useUserDispatch(), useQueryClient());

/**
 * The whole user and its actions. Rerenders on any change to the user (a
 * tron reward, a profile edit): prefer useCurrentUserId, useIsAuthenticated,
 * useUserIdentity or useUserActions when they are enough.
 */
const useUser = () => {
  const { state, dispatch } = useUserState();
  const queryClient = useQueryClient();

  return {
    currentUser: state.user,
    ...createUserActions(dispatch, queryClient)
  };
};

export default useUser;
